/**
 * Account identity context (Final Phase Stage B). One internal account_id per user,
 * fed by any login method (Sui SIWS, EVM SIWE, or Solana). This is
 * the identity source of truth for the app; useCurrentAccount stays for Sui-only UX
 * (withdraw, Sui balance). The 37-file read cutover is Stage B3.
 */
import { createContext, useContext } from 'react';

/** OAuth providers offered in the sign-in window. Each must be enabled in the Supabase dashboard. */
export type OAuthProvider = 'google' | 'discord' | 'github' | 'apple' | 'x' | 'facebook';

export interface AccountContextValue {
  accountId: string | null;
  token: string | null;
  /**
   * The player key for this identity: the account_id for a Supabase (web2) account — the
   * value its rows carry in the wallet-named columns — or the wallet proven at a parked
   * wallet login.
   */
  address: string | null;
  isAuthenticated: boolean;
  /** Private sign-in identity, shown only on the current player's profile. */
  email?: string | null;
  displayName?: string | null;
  /**
   * Sign in with the connected Sui wallet (SIWS). Pass `overrideAddress` with the account
   * just returned from `connectWallet` when signing in immediately after connecting — the
   * `useCurrentAccount()` hook hasn't re-rendered yet at that point. Throws if neither an
   * override nor a connected Sui wallet is available.
   */
  loginWithSui: (overrideAddress?: string) => Promise<void>;
  /** Connect (if needed) + sign in with an EVM wallet (SIWE). */
  loginWithEvm: () => Promise<void>;
  /** Sign in with the connected Solana wallet (ed25519). Throws if none is connected. */
  loginWithSolana: () => Promise<void>;
  /** True when Supabase Auth is configured for this build (email / OAuth sign-in available). */
  supabaseAuthEnabled: boolean;
  /**
   * Email the player a one-time sign-in code (Supabase Auth). The same email also carries
   * a magic link, which lands back on the app and completes sign-in on its own.
   */
  sendEmailCode: (email: string) => Promise<void>;
  /** Complete an email sign-in with the code from the email → account session. */
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  /**
   * Start an OAuth sign-in (Google, Discord, …). Redirects away; sign-in completes when the
   * provider redirects back and the Supabase session is exchanged for an account session.
   */
  loginWithOAuth: (provider: OAuthProvider) => Promise<void>;
  /**
   * True inside the CrazyGames frame once their SDK reports accounts available: there,
   * Login with CrazyGames is the only sign-in (lib/portalEmbed.ts).
   */
  crazyGamesLoginAvailable?: boolean;
  /** Open CrazyGames' login prompt and sign in to the linked Suikamon account. */
  loginWithCrazyGames?: () => Promise<void>;
  logout: () => void;
  /**
   * True for the single render-session immediately after a brand-new wallet logs in (the
   * `is_new` signal from /verify). Transient — not persisted in the session. Drives the
   * one-time new-wallet activation modal; cleared by `dismissActivation()`.
   */
  activationPending: boolean;
  /** Clear `activationPending` once the activation flow has been shown/handled. */
  dismissActivation: () => void;
}

export const AccountContext = createContext<AccountContextValue | null>(null);

export function useAccountId(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccountId must be used within <AccountProvider>');
  return ctx;
}
